import { useQueryClient } from '@tanstack/react-query';

import { Spinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import { ContentLeft, ContentRight, Grid } from '@components/page-containers/page-containers';

import { useGetActiveFollowers, useGetJob, useUpdateFollowers } from 'api/jobs/hooks';
import { ApplicationFollower, Follower, FollowerResponse } from 'api/jobs/requests';
import { useManageJobAccess } from 'hooks/use-manage-job-access';
import { HiringTeam, LimitedAccess } from 'pages/CreateJobNew/Access/access';
import { Job } from 'types/jobs';

export const Access = ({ jobId }: { jobId: number }) => {
  const followerQuery = useGetActiveFollowers(jobId);
  const jobQuery = useGetJob(jobId);

  if (!followerQuery.data || !jobQuery.data) {
    return <Spinner />;
  }
  return <RenderAccess followerData={followerQuery.data} job={jobQuery.data} />;
};

export const RenderAccess = ({
  followerData,
  job,
}: {
  followerData: FollowerResponse;
  job: Job;
}) => {
  const queryClient = useQueryClient();
  const hasManageAccess = useManageJobAccess(job.createdById);
  const saveMutation = useUpdateFollowers();
  const followers = followerData.followers ?? [];
  const appFollowers = followerData.appFollowers ?? [];
  const combinedFollowerIds = [...followers, ...appFollowers].map((follower) => follower.id);

  const handleSave = (followerIds: number[], appFollowerIds: number[]) => {
    saveMutation.mutate({
      jobId: job.id,
      followerIds,
      appFollowerIds,
    });
  };

  const appendFollower = (follower: Follower) => {
    const followerData = queryClient.getQueryData<FollowerResponse>(['job', 'followers', job.id]);
    if (followerData === undefined) {
      return;
    }
    const newFollowers = [...followerData.followers.map((f) => f.id), follower.id];
    handleSave(
      newFollowers,
      followerData.appFollowers.map((follower) => follower.id),
    );
  };

  const removeFollower = (userId: number) => {
    const newFollowers = followers.filter((f) => f.id !== userId).map((f) => f.id);
    handleSave(
      newFollowers,
      appFollowers.map((f) => f.id),
    );
  };

  const appendAppFollower = (follower: ApplicationFollower) => {
    const followerData = queryClient.getQueryData<FollowerResponse>(['job', 'followers', job.id]);
    if (followerData === undefined) {
      return;
    }

    const newFollowers = [...followerData.appFollowers.map((f) => f.id), follower.id];
    handleSave(
      followerData.followers.map((f) => f.id),
      newFollowers,
    );
  };

  const removeAppFollower = (userId: number) => {
    const newFollowers = appFollowers.filter((f) => f.id !== userId).map((f) => f.id);
    handleSave(
      followers.map((f) => f.id),
      newFollowers,
    );
  };

  return (
    <PageContent>
      <Grid>
        <ContentLeft>
          <HiringTeam
            followers={followers}
            combinedFollowerIds={combinedFollowerIds}
            appendFollower={appendFollower}
            removeFollower={removeFollower}
            hasManageAccess={hasManageAccess}
          />
        </ContentLeft>
        <ContentRight>
          <LimitedAccess
            appFollowers={appFollowers}
            combinedFollowerIds={combinedFollowerIds}
            appendAppFollower={appendAppFollower}
            removeAppFollower={removeAppFollower}
            job={job}
            hasManageAccess={hasManageAccess}
          />
        </ContentRight>
      </Grid>
    </PageContent>
  );
};
